import type {
  DeadSubscription,
  DeadSubscriptionAction,
  DeadSubscriptionOutcome,
  DeadSubscriptionPaymentMethod,
} from "../types/deadSubscriptions";

// Pure helpers of /admin/dead-subscriptions.

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * "Unpaid for" filter presets, in days since the oldest invoice still owed.
 * Stripe retries a failed renewal for a few weeks, so the default leaves
 * those still in dunning out of the batch.
 */
export const UNPAID_FOR_OPTIONS = [
  { value: 0, label: "Any" },
  { value: 14, label: "14d+" },
  { value: 30, label: "30d+" },
  { value: 60, label: "60d+" },
  { value: 90, label: "90d+" },
  { value: 180, label: "180d+" },
] as const;

export type UnpaidForDays = (typeof UNPAID_FOR_OPTIONS)[number]["value"];

export const DEFAULT_UNPAID_FOR_DAYS: UnpaidForDays = 30;

export const SORT_OPTIONS = [
  { value: "owed", label: "Owed" },
  { value: "unpaidSince", label: "Unpaid longest" },
  { value: "paidTotal", label: "Paid most" },
] as const;

export type DeadSubscriptionSort = (typeof SORT_OPTIONS)[number]["value"];

/** klap-api's per-request cap: bigger selections go in several requests. */
export const ACTION_CHUNK_SIZE = 25;

/** Whole days between an ISO date and now (null when there's no date). */
export function daysSince(iso: string | null, now: number): number | null {
  if (!iso) return null;
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return null;
  return Math.max(0, Math.floor((now - time) / DAY_MS));
}

export type DeadSubscriptionFilters = {
  minUnpaidDays: number;
  includeApi: boolean;
  search: string;
};

export function filterDeadSubscriptions(
  rows: DeadSubscription[],
  { minUnpaidDays, includeApi, search }: DeadSubscriptionFilters,
  now: number,
): DeadSubscription[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (!includeApi && row.isApi) return false;
    if (minUnpaidDays > 0 && (daysSince(row.unpaidSince, now) ?? 0) < minUnpaidDays)
      return false;
    if (!needle) return true;
    return [row.email, row.name, row.subscriptionId, row.customerId, row.product].some(
      (field) => field?.toLowerCase().includes(needle),
    );
  });
}

export function sortDeadSubscriptions(
  rows: DeadSubscription[],
  sort: DeadSubscriptionSort,
): DeadSubscription[] {
  const time = (iso: string | null) => (iso ? new Date(iso).getTime() : Infinity);
  const sorted = [...rows];
  if (sort === "owed") sorted.sort((a, b) => b.owed - a.owed);
  if (sort === "paidTotal") sorted.sort((a, b) => b.paidTotal - a.paidTotal);
  if (sort === "unpaidSince")
    sorted.sort((a, b) => time(a.unpaidSince) - time(b.unpaidSince));
  return sorted;
}

/** `$29.00`, `€1,234.50` (amounts in minor units). */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Sum of an amount over rows of mixed currencies, one figure per currency
 * (largest first): `$1,234.00 + €56.00`, or `—` when there's nothing.
 */
export function formatTotals<T extends { currency: string | null }>(
  rows: T[],
  amount: (row: T) => number,
): string {
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (!row.currency) continue;
    totals.set(row.currency, (totals.get(row.currency) ?? 0) + amount(row));
  }
  const parts = [...totals.entries()]
    .filter(([, total]) => total > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([currency, total]) => formatMoney(total, currency));
  return parts.length ? parts.join(" + ") : "—";
}

/** `$29 / mo`, `$290 / yr`; null for metered prices. */
export function formatPrice(row: DeadSubscription): string | null {
  if (row.price === null) return null;
  const unit = { day: "day", week: "wk", month: "mo", year: "yr" }[row.interval ?? "month"];
  const amount = formatMoney(row.price, row.currency).replace(/\.00$/, "");
  return `${amount} / ${unit}`;
}

export function isPaymentMethodExpired(
  pm: DeadSubscriptionPaymentMethod | null,
  now: number,
): boolean {
  if (!pm?.expYear || !pm.expMonth) return false;
  const date = new Date(now);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  return pm.expYear < year || (pm.expYear === year && pm.expMonth < month);
}

const PAYMENT_METHOD_TYPE_LABEL: Record<string, string> = {
  card: "Card",
  link: "Link",
  paypal: "PayPal",
  sepa_debit: "SEPA debit",
  us_bank_account: "US bank account",
};

/** `Visa •••• 4242 · 04/27`, `Link`, `SEPA debit •••• 3000`; `None` when absent. */
export function formatPaymentMethod(pm: DeadSubscriptionPaymentMethod | null): string {
  if (!pm) return "None";
  const name = pm.brand
    ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1)
    : (PAYMENT_METHOD_TYPE_LABEL[pm.type] ?? pm.type);
  const last4 = pm.last4 ? ` •••• ${pm.last4}` : "";
  const expiry =
    pm.expMonth && pm.expYear
      ? ` · ${String(pm.expMonth).padStart(2, "0")}/${String(pm.expYear).slice(-2)}`
      : "";
  return `${name}${last4}${expiry}`;
}

export const OUTCOME_LABEL: Record<DeadSubscriptionOutcome, string> = {
  charged: "Charged",
  pending: "Processing",
  charge_failed: "Declined",
  canceled: "Canceled",
  skipped: "Skipped",
  error: "Error",
};

export const ACTION_COPY: Record<
  DeadSubscriptionAction,
  { button: string; title: string; running: string }
> = {
  charge: {
    button: "Retry charge",
    title: "Retry the payment",
    running: "Charging",
  },
  charge_or_cancel: {
    button: "Charge, cancel if declined",
    title: "Charge, and cancel when declined",
    running: "Charging",
  },
  cancel: {
    button: "Cancel subscriptions",
    title: "Cancel subscriptions",
    running: "Canceling",
  },
};

export function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

export const stripeCustomerUrl = (customerId: string) =>
  `https://dashboard.stripe.com/customers/${encodeURIComponent(customerId)}`;

export const stripeSubscriptionUrl = (subscriptionId: string) =>
  `https://dashboard.stripe.com/subscriptions/${encodeURIComponent(subscriptionId)}`;

export const deadSubscriptionsUrl = (
  apiUrl: string | undefined,
  path: "" | "/charge" | "/cancel" = "",
  refresh = false,
) => `${apiUrl ?? ""}/internal/dead-subscriptions${path}${refresh ? "?refresh=1" : ""}`;
